// Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import * as React from "react";
import { useGetApi } from "../../../data/hooks/api";
import { RESOURCE_SERVER_URLS } from "../../../data/configs/spl-config";
import {
   Tabs,
   Tab,
   Typography,
   TableRow,
   Table,
   TableCell,
   TableBody,
   Paper,
   Stack,
   Box,
   Button,
   Tooltip,
} from "@mui/material";
import ListProjectContacts from "./ListProjectContacts";
import * as Types from "../../../data/utils/types";
import ListProjectCases from "./ListProjectCases";
import LinearLoading from "../../LinearLoading";
import { ErrorComponent } from "../../../pages/ErrorPage";
import { ItemNotFoundComponent } from "../../../pages/ItemNotFound";
import {
   PROJECT_TYPE_CLOUD_SUPPORT,
   PROJECT_TYPE_CLOUD_EVALUATION_SUPPORT,
} from "../../../constants/constants";
import { useNavigate } from "react-router-dom";
import { Store } from "react-notifications-component";
import PathView from "../../PathView";
import { Summarize } from "@mui/icons-material";

interface TabPanelProps {
   children: React.ReactNode;
   value: number;
   index: number;
}

interface ListProjectDetailProps {
   id: string;
}

export default function ListProjectDetail({
   id,
}: ListProjectDetailProps): JSX.Element {
   const [value, setTabValue] = React.useState(0);
   const apiUrl = RESOURCE_SERVER_URLS[0] + `/projects/${id}`;
   const [isTypeCloud, setIsTypeCloud] = React.useState(false);

   const { data, loading, error, getApiData } = useGetApi<
      Types.ProjectDetails
   >({
      url: apiUrl,
      headers: { accept: "application/json" },
   });

   React.useEffect(() => {
      getApiData();
   }, []);

   React.useEffect(() => {
      setIsTypeCloud(
         data?.projectType == PROJECT_TYPE_CLOUD_SUPPORT ||
         data?.projectType == PROJECT_TYPE_CLOUD_EVALUATION_SUPPORT
      );
   }, [data]);

   const handleTabChange = (
      event: React.SyntheticEvent,
      newValue: number
   ) => {
      setTabValue(newValue);
   };

   return (
      <>
         {loading
            ? <LinearLoading />
            : error
               ? error.status == 404
                  ? <ItemNotFoundComponent />
                  : <ErrorComponent />
               : data && (
                  <>
                     <PathView
                        accountName={data?.accountName}
                        accountNumber={data?.accountNumber}
                        projectKey={data?.key}
                     />
                     <div>
                        <ProjectHeading data={data}></ProjectHeading>
                     </div>
                     <Tabs
                        value={value}
                        onChange={handleTabChange}
                        aria-label="Project Tabs"
                     >
                        <Tab label="Basic Info" />
                        <Tab label="Contacts" />
                        <Tab label="Cases" />
                     </Tabs>
                     <Paper>
                        <TabPanel value={value} index={0}>
                           <Typography component="div">
                              <BasicInfoTable data={data} />
                           </Typography>
                        </TabPanel>
                        <TabPanel value={value} index={1}>
                           <Typography component="div">
                              <ListProjectContacts id={id} />
                           </Typography>
                        </TabPanel>
                        <TabPanel value={value} index={2}>
                           <Typography component="div">
                              <ListProjectCases id={id} isTypeCloud={isTypeCloud} />
                           </Typography>
                        </TabPanel>
                     </Paper>
                  </>
               )
         }
      </>
   );
}

const TabPanel = ({ children, value, index }: TabPanelProps) => {
   return (
      <div role="tabpanel" hidden={value !== index}>
         {value === index && (
            <Typography component="div" sx={{ p: 3 }}>
               {children}
            </Typography>
         )}
      </div>
   );
};

const ProjectHeading = ({ data }: { data: Types.ProjectDetails }) => {
   const name = data && data.name ? data.name : "Loading...";
   const key = data && data.key ? data.key : " Project";

   const handleSLAReportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();

      if (data?.sysId !== "") {
         window.open(`/support/projects/${data?.number}/sla-report/${data?.sysId}`);
      } else {
         Store.addNotification({
            title: "Error!",
            message: "Something went wrong! Please try again.",
            type: "danger",
            insert: "top",
            container: "top-right",
            animationIn: ["animate__animated", "animate__fadeIn"],
            animationOut: ["animate__animated", "animate__fadeOut"],
            dismiss: {
               duration: 5000,
               onScreen: true,
            },
         });
      }
   };

   const handleTimelogReportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();

      if (data?.sysId !== "") {
         window.open(`/support/projects/${data?.number}/timelogs-report`);
      } else {
         Store.addNotification({
            title: "Error!",
            message: "Something went wrong! Please try again.",
            type: "danger",
            insert: "top",
            container: "top-right",
            animationIn: ["animate__animated", "animate__fadeIn"],
            animationOut: ["animate__animated", "animate__fadeOut"],
            dismiss: {
               duration: 5000,
               onScreen: true,
            },
         });
      }
   };
   
   const handleCSReportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();

      if (data?.sysId !== "") {
         window.open(`/support/projects/${data?.number}/cs-report/${data?.sysId}`);
      } else {
         Store.addNotification({
            title: "Error!",
            message: "Something went wrong! Please try again.",
            type: "danger",
            insert: "top",
            container: "top-right",
            animationIn: ["animate__animated", "animate__fadeIn"],
            animationOut: ["animate__animated", "animate__fadeOut"],
            dismiss: {
               duration: 5000,
               onScreen: true,
            },
         });
      }
   };

   return (
      <Stack spacing={0}>
         <Box flex={2} display="flex" justifyContent="center" >
            <Typography variant="h4" gutterBottom fontWeight={'bold'}>
               {name + "   ( " + key + " )"}
            </Typography>
         </Box>
         <Box flex={2} display="flex" justifyContent="flex-end" marginRight={"5%"} >
            <Button variant="contained"
               sx={{
                  color: 'white',
                  marginBottom: '-10px',
                  ':hover': {
                     bgcolor: '#e96900',
                     borderColor: 'primary.main',
                     color: 'white',
                  }
               }}
               onClick={handleSLAReportClick}
               startIcon={<Summarize />}
            >
               SLA Report
            </Button>
            <Button variant="contained"
               sx={{
                  color: 'white',
                  marginBottom: '-10px',
                  marginLeft: '15px',
                  ':hover': {
                     bgcolor: '#e96900',
                     borderColor: 'primary.main',
                     color: 'white',
                  }
               }}
               onClick={handleTimelogReportClick}
               startIcon={<Summarize />}
            >
               Time Report
            </Button>
            <Button variant="contained"
               sx={{
                  color: 'white',
                  marginBottom: '-10px',
                  marginLeft: '15px',
                  ':hover': {
                     bgcolor: '#e96900',
                     borderColor: 'primary.main',
                     color: 'white',
                  }
               }}
               onClick={handleCSReportClick}
               startIcon={<Summarize />}
            >
               CS Report
            </Button>
         </Box>
      </Stack>
   );
};

const BasicInfoTable = ({ data }: { data: Types.ProjectDetails }) => {
   const state = (data && data.closureState) || "N/A";
   const startDate = (data && data.startDate) || "N/A";
   const endDate = (data && data.endDate) || "N/A";
   const remainingQueryHours = (data && data.remainingQueryHours) || "N/A";
   const accountNumber = (data && data.accountNumber) || "N/A";
   const accountName = (data && data.accountName) || accountNumber;
   const totalQueryHours = (data && data.totalQueryHours) || "N/A";

   return (
      <div className="table-container" style={{ display: "flex" }}>
         <div style={{ flex: 1, marginRight: "10rem" }}>
            <Table className="table" style={{ width: "100%" }}>
               <TableBody>
                  <TableRow className="row-style">
                     <TableCell variant="head" style={{ border: "none" }}>
                        State:
                     </TableCell>
                     <TableCell style={{ border: "none" }}>{state}</TableCell>
                  </TableRow>
                  <TableRow className="row-style">
                     <TableCell variant="head" style={{ border: "none" }}>
                        Start Date:
                     </TableCell>
                     <TableCell style={{ border: "none" }}>{startDate}</TableCell>
                  </TableRow>
                  <TableRow className="row-style">
                     <TableCell variant="head" style={{ border: "none" }}>
                        End Date:
                     </TableCell>
                     <TableCell style={{ border: "none" }}>{endDate}</TableCell>
                  </TableRow>
               </TableBody>
            </Table>
         </div>
         <div style={{ flex: 1 }}>
            <Table className="table" style={{ width: "60%" }}>
               <TableBody>
                  <TableRow className="row-style">
                     <TableCell variant="head" style={{ border: "none" }}>
                        Total Query Hours:
                     </TableCell>
                     <TableCell style={{ border: "none" }}>
                        {totalQueryHours}
                     </TableCell>
                  </TableRow>
                  <TableRow className="row-style">
                     <TableCell variant="head" style={{ border: "none" }}>
                        Remaining Query Hours:
                     </TableCell>
                     <TableCell style={{ border: "none" }}>
                        {remainingQueryHours}
                     </TableCell>
                  </TableRow>
               </TableBody>
            </Table>
         </div>
      </div>
   );
};
